import { useEffect } from 'react';
import { Navigate, Route, useLocation } from 'react-router-dom';
import { pathAfter } from './path-after';
import { RedirectDatum, RouteDatum } from './types';

/**
 * Creates route a route element for a given route, and recursively nested routes for all
 * optional path parameters. Helps handling optional params easily in React Router v6.
 *
 * In React Router v5 you could directly have <Route path='company/:companyId/:companyName?/:viewMode?' element={<CompanyView />} />
 *
 * In React Router v6 you'll need a tree:
 * <Route path='company/:companyId' element={<CompanyView />}>
 *   <Route path=':companyName'>
 *     <Route path=':viewMode' />
 *   </Route>
 * </Route>
 *
 * This function builds such a tree.
 */
function getRoute(
  route: Omit<RouteDatum, 'element'> & { element: JSX.Element | null },
  keyPrefix?: string,
): JSX.Element {
  const { path, element, optionalParams } = route;
  const key = keyPrefix ? `${keyPrefix}-${path}` : path;
  const subrouteExtensionMaybe = optionalParams?.length ? `/*` : '';
  return (
    <Route key={key} path={`${path}${subrouteExtensionMaybe}`} element={element}>
      {optionalParams?.map((param, index) => {
        return getRoute({ path: `:${param}`, element: null, optionalParams: optionalParams.slice(index + 1) });
      })}
    </Route>
  );
}

export function getRoutes(routeData: RouteDatum[], keyPrefix?: string) {
  return routeData.map(route => getRoute(route, keyPrefix));
}

export function getRedirectRoute(redirect: RedirectDatum) {
  const path = redirect.type === 'moved' ? `${redirect.from}/*` : redirect.from;
  const key = `redirect-${redirect.from.replace('/', '')}`;
  const element = (() => {
    switch (redirect.type) {
      case 'internal':
        return <Navigate to={redirect.to} replace />;
      case 'external':
        return <RedirectExternal to={redirect.to} />;
      case 'moved':
        return <RedirectKeepingLocation to={redirect.to} from={redirect.from} />;
    }
  })();
  return <Route path={path} key={key} element={element} />;
}

/**
 * Replaces the current URL with `to`, keeping its query string and hash. With `from`, whatever
 * follows `from` in the current path is appended too, so `/old/a/b?x=1#y` lands on `/new/a/b?x=1#y`.
 *
 * The rest of the path is sliced off the raw pathname rather than read from the route's `*` param,
 * which react-router hands over decoded: an id holding an encoded `/` would otherwise come out as
 * two segments. `replace`, so Back skips the old URL instead of bouncing onto the new one again.
 */
export function RedirectKeepingLocation(props: { to: string; from?: string }) {
  const { pathname, search, hash } = useLocation();
  const subPath = props.from === undefined ? '' : pathAfter(pathname, props.from);
  return <Navigate to={{ pathname: `${props.to}${subPath}`, search, hash }} replace />;
}

const RedirectExternal = (props: { to: string }) => {
  useEffect(() => {
    window.location.href = props.to;
  }, [props.to]);

  return <></>;
};
